import React, { useState, useEffect, useRef, useMemo } from 'react';
import { m } from 'framer-motion';
import { FiPlus, FiMinus, FiShoppingBag, FiLoader, FiChevronDown } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useTheme } from '../../context/ThemeContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from 'react-i18next';
import { useMotionConfig } from '../../hooks/useReducedMotion';
import { STRIPE_CONFIG } from '../../config/stripe';
import { reduceBundle } from '../../utils/bundleReduce';
import { findBestPricingMatch } from './editFlowUtils';

const API_BASE = STRIPE_CONFIG.apiBaseUrl || '/api';

// Extract size value from a sizes array entry, which may be a string or an object
const getSizeValue = (s) => {
  if (typeof s === 'string') return s;
  return s?.value ?? s?.name ?? s?.size ?? s?.label;
};

// bundle_components is JSONB; usually an array, but guard against a JSON string.
const normalizeComponents = (raw) => {
  let value = raw;
  if (typeof value === 'string') {
    try { value = JSON.parse(value); } catch { return null; }
  }
  return Array.isArray(value) && value.length > 0 ? value : null;
};

function getProductDepositAmount(product, depositRules, getDepositAmount) {
  if (!depositRules || depositRules.length === 0) return 0;
  return getDepositAmount ? getDepositAmount(product.category_slug || '') : 0;
}

export default function AddProductsSection({
  booking,
  locationId,
  startDate,
  endDate,
  onAddProduct,
  onRemoveProduct,
  addedProducts,
  editedItems,
  depositRules,
  depositSwapBudget,
  getDepositAmount
}) {
  const theme = useTheme();
  const { formatCurrency } = useCurrency();
  const { getProductTranslation } = useLanguage();
  const { t } = useTranslation();
  const motion = useMotionConfig();

  const [availableProducts, setAvailableProducts] = useState([]);
  const [availabilityEntries, setAvailabilityEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!expanded || fetchedRef.current) return;
    fetchedRef.current = true;

    const fetchProductsAndAvailability = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ locationId });
        const res = await fetch(`${API_BASE}/product-types?${params}`);
        if (!res.ok) return;
        const data = await res.json();

        const allProducts = data.productTypes || [];
        const products = allProducts;

        console.log('[AddProducts] All product types:', products.map(p => ({ id: p.id, name: p.product_name })));

        if (!startDate || !endDate) {
          setAvailableProducts(products);
          setLoading(false);
          return;
        }

        const start = new Date(startDate).toISOString();
        const end = new Date(endDate).toISOString();

        const [pricingResults, availData] = await Promise.all([
          Promise.all(
            products.map(async (product) => {
              try {
                const priceParams = new URLSearchParams({
                  productTypeId: product.id,
                  locationId,
                  startDate: start
                });
                const priceRes = await fetch(`${API_BASE}/calculate-price?${priceParams}`);
                if (!priceRes.ok) return { ...product, calculatedPrice: null };
                const priceData = await priceRes.json();
                const matchingRule = findMatchingPricingRule(priceData.pricingOptions, startDate, endDate);
                return { ...product, calculatedPrice: matchingRule };
              } catch {
                return { ...product, calculatedPrice: null };
              }
            })
          ),
          // Free THIS booking's own items so they count as available again when
          // measuring what's addable, via excludeBookingId (p_excluded_booking_id):
          // the RPC then ignores this booking in overlap detection.
          //
          // We deliberately do NOT pass the booking's items as
          // excludedInventoryItemIds. That RPC parameter does the OPPOSITE of
          // freeing — `i.id != ALL(p_excluded_inventory_item_ids)` REMOVES those
          // items from the available pool (its purpose is hiding items reserved
          // by an in-progress basket). Passing the booking's own items there hid
          // them, so the booking's bikes (e.g. the junior inside an existing
          // bundle) looked sold out. The cart-consumption accounting below
          // re-subtracts exactly what the edit keeps/adds.
          fetchAvailability(locationId, start, end, booking?.id || null, null)
        ]);

        const availResult = availData.map;
        setAvailabilityEntries(availData.entries);

        console.log('[AddProducts] Availability map:', availResult);
        console.log('[AddProducts] Pricing results:', pricingResults.map(p => ({ id: p.id, name: p.product_name, hasPrice: !!p.calculatedPrice })));

        const withPricing = pricingResults.filter(p => p.calculatedPrice);
        const withAvailability = withPricing.filter(p => {
          const hasSizes = p.sizes && Array.isArray(p.sizes) && p.sizes.length > 0;
          if (hasSizes) {
            const sizeCounts = p.sizes.map(s => {
              const sv = getSizeValue(s);
              return { size: sv, key: `${p.id}_${sv}`, count: availResult[`${p.id}_${sv}`] || 0 };
            });
            console.log('[AddProducts] Sized product', p.product_name, 'sizes:', sizeCounts);
            return p.sizes.some(s => (availResult[`${p.id}_${getSizeValue(s)}`] || 0) > 0);
          }
          const count = availResult[`${p.id}_null`] || availResult[`${p.id}_`] || 0;
          console.log('[AddProducts] Unsized product', p.product_name, 'key:', `${p.id}_null`, 'count:', count);
          return count > 0;
        });

        console.log('[AddProducts] With pricing:', withPricing.map(p => p.product_name));
        console.log('[AddProducts] With availability (final):', withAvailability.map(p => p.product_name));

        setAvailableProducts(withAvailability);
      } catch {
        setAvailableProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProductsAndAvailability();
  }, [expanded, locationId, startDate, endDate]);

  useEffect(() => {
    fetchedRef.current = false;
  }, [startDate, endDate]);

  const getAddedQty = (productId, size) => {
    return (addedProducts || [])
      .filter(p => p.product_type_id === productId && (p.size || null) === (size || null))
      .reduce((sum, p) => sum + p.quantity, 0);
  };

  const getBookedQty = (productId, size) => {
    return (editedItems || [])
      .filter(i => i.product_type_id === productId && (i.size || null) === (size || null) && i.quantity > 0)
      .reduce((sum, i) => sum + i.quantity, 0);
  };

  const getSelectedQty = (productId, size) => {
    return getBookedQty(productId, size) + getAddedQty(productId, size);
  };

  // Per-product supply from /api/availability (the booking's own items are
  // already freed via excludedInventoryItemIds), keyed by product type → size.
  const byProduct = useMemo(() => {
    const m = {};
    for (const e of (availabilityEntries || [])) {
      const id = e.product_type_id;
      if (!m[id]) {
        m[id] = {
          inventoryType: e.inventory_type,
          isBundle: !!e.is_bundle,
          bundleComponents: normalizeComponents(e.bundle_components),
          bySize: {}
        };
      }
      const sizeKey = e.size != null ? e.size : 'null';
      m[id].bySize[sizeKey] = (m[id].bySize[sizeKey] || 0) + (e.available_count || 0);
    }
    return m;
  }, [availabilityEntries]);

  const bundleComponentsById = useMemo(() => {
    const m = {};
    for (const [id, p] of Object.entries(byProduct)) {
      if (p.isBundle && p.bundleComponents) m[id] = p.bundleComponents;
    }
    return m;
  }, [byProduct]);

  // What the in-progress cart consumes of each *component* product type, split
  // into fixed-size vs size-less buckets. Existing bundles are already
  // materialized as their component rows in editedItems (counted directly, NOT
  // expanded); newly added bundles have no component rows, so they are expanded
  // via their bundle_components. This is what makes bundle ↔ component
  // availability reactive and consistent.
  const consumption = useMemo(() => {
    const m = {}; // id -> { bySize: {size: qty}, sizeless: qty }
    const add = (id, size, qty) => {
      if (!id || qty <= 0) return;
      if (!m[id]) m[id] = { bySize: {}, sizeless: 0 };
      if (size == null || size === '') m[id].sizeless += qty;
      else m[id].bySize[size] = (m[id].bySize[size] || 0) + qty;
    };
    for (const i of (editedItems || [])) {
      if (i.quantity > 0) add(i.product_type_id, i.size || null, i.quantity);
    }
    for (const a of (addedProducts || [])) {
      if (a.quantity <= 0) continue;
      const comps = bundleComponentsById[a.product_type_id];
      if (comps) {
        for (const c of comps) add(c.product_type_id, c.size || null, (Number(c.quantity) || 1) * a.quantity);
      } else {
        add(a.product_type_id, a.size || null, a.quantity);
      }
    }
    return m;
  }, [editedItems, addedProducts, bundleComponentsById]);

  // Remaining supply for a component (availability − cart consumption), shaped
  // for the shared reduceBundle()/standalone math.
  const getSupplyFor = (productTypeId) => {
    const p = byProduct[productTypeId];
    if (!p) return { totalAvailable: 0, availableBySize: new Map(), inventoryType: null };
    const consumed = consumption[productTypeId] || { bySize: {}, sizeless: 0 };
    const availableBySize = new Map();
    let gross = 0;
    let fixedConsumed = 0;
    for (const [sizeKey, count] of Object.entries(p.bySize)) {
      gross += count;
      if (sizeKey === 'null' || sizeKey === '') continue; // size-less supply only feeds the total
      const c = consumed.bySize[sizeKey] || 0;
      fixedConsumed += c;
      availableBySize.set(sizeKey, Math.max(0, count - c));
    }
    const totalAvailable = Math.max(0, gross - fixedConsumed - consumed.sizeless);
    return { totalAvailable, availableBySize, inventoryType: p.inventoryType };
  };

  const getAvailCount = (productId, size) => {
    const comps = bundleComponentsById[productId];
    if (comps) {
      // Bundle: reduce over remaining component supply. The reduction already
      // accounts for everything the cart commits (including this bundle's own
      // selected copies), so do NOT subtract getSelectedQty again.
      return reduceBundle(comps, getSupplyFor).bundleCount;
    }
    const supply = getSupplyFor(productId);
    if (size == null) return supply.totalAvailable;
    return Math.max(0, supply.availableBySize.get(size) || 0);
  };

  const isDepositBlocked = (product) => {
    if (!depositRules || depositRules.length === 0) return false;
    const cost = getProductDepositAmount(product, depositRules, getDepositAmount);
    if (cost === 0) return false;
    return (depositSwapBudget || 0) < cost;
  };

  if (!expanded) {
    return (
      <m.div {...motion.fadeInUp}>
        <button
          onClick={() => setExpanded(true)}
          className="w-full bg-white rounded-2xl shadow-sm border border-dashed border-gray-300 p-5 flex items-center justify-center gap-2 text-sm font-medium hover:border-gray-400 hover:bg-gray-50 transition-colors"
          style={{ color: theme.colors.primary.base }}
        >
          <SafeIcon icon={FiShoppingBag} className="w-5 h-5" />
          {t('manage.addProducts', 'Add more products')}
        </button>
      </m.div>
    );
  }

  return (
    <m.div {...motion.fadeInUp} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <SafeIcon icon={FiShoppingBag} className="w-5 h-5" style={{ color: theme.colors.primary.base }} />
        {t('manage.addProducts', 'Add more products')}
      </h2>

      {loading ? (
        <div role="status" aria-live="polite" className="flex items-center justify-center py-8 gap-2 text-gray-600">
          <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
          <span className="text-sm">{t('manage.loadingProducts', 'Loading products...')}</span>
        </div>
      ) : availableProducts.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">
          {t('manage.noAdditionalProducts', 'No additional products available.')}
        </p>
      ) : (
        <div className="space-y-3">
          {availableProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              getAddedQty={getAddedQty}
              getAvailCount={getAvailCount}
              getSelectedQty={getSelectedQty}
              onAddProduct={onAddProduct}
              onRemoveProduct={onRemoveProduct}
              formatCurrency={formatCurrency}
              getProductTranslation={getProductTranslation}
              theme={theme}
              t={t}
              depositBlocked={isDepositBlocked(product)}
            />
          ))}
        </div>
      )}
    </m.div>
  );
}

function AvailabilityLabel({ avail, selected, t }) {
  if (selected > 0 && avail === 0) {
    return (
      <span className="text-xs font-medium text-orange-600">
        {t('manage.maximumSelected', 'Maximum selected')}
      </span>
    );
  }
  if (avail === 0 && selected === 0) {
    return (
      <span className="text-xs font-medium text-red-500">
        {t('manage.soldOut', 'Sold out')}
      </span>
    );
  }
  return (
    <span className="text-xs font-medium text-green-600">
      {t('manage.availableCount', '{{count}} available', { count: avail })}
      {selected > 0 && (
        <span className="text-gray-500 font-normal">
          {' · '}{t('manage.selectedCount', '{{count}} selected', { count: selected })}
        </span>
      )}
    </span>
  );
}

function ProductCard({
  product,
  getAddedQty,
  getAvailCount,
  getSelectedQty,
  onAddProduct,
  onRemoveProduct,
  formatCurrency,
  getProductTranslation,
  theme,
  t,
  depositBlocked
}) {
  const productName = getProductTranslation(
    product.product_name, product.translations, 'product_name'
  );
  const price = product.calculatedPrice?.price;
  const hasSizes = product.sizes && Array.isArray(product.sizes) && product.sizes.length > 0;
  const variantLabel = product.variant_label || t('manage.selectSize', 'Select size');

  if (!hasSizes) {
    const avail = getAvailCount(product.id, null);
    const qty = getAddedQty(product.id, null);
    const selected = getSelectedQty(product.id, null);
    const canAdd = avail > 0 && !depositBlocked;

    return (
      <div className="p-3 bg-gray-50 rounded-xl">
        <div className="flex items-center gap-3">
          {product.picture_url && (
            <img
              src={product.picture_url}
              alt={productName}
              className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 truncate text-sm">{productName}</p>
            <div className="flex items-center gap-2">
              {price != null && (
                <span className="text-xs text-gray-500">
                  {formatCurrency(price)}/{t('manage.each', 'ea')}
                </span>
              )}
              <AvailabilityLabel avail={avail} selected={selected} t={t} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            {qty > 0 && (
              <button
                onClick={() => onRemoveProduct(product.id, null)}
                className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
              >
                <SafeIcon icon={FiMinus} className="w-4 h-4 text-gray-600" />
              </button>
            )}
            <span className="w-8 text-center font-semibold text-gray-900">{qty}</span>
            <button
              onClick={() => onAddProduct(product, null)}
              disabled={!canAdd}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-colors text-white disabled:opacity-30"
              style={{ backgroundColor: theme.colors.primary.base }}
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SizedProductCard
      product={product}
      productName={productName}
      price={price}
      variantLabel={variantLabel}
      getAddedQty={getAddedQty}
      getAvailCount={getAvailCount}
      getSelectedQty={getSelectedQty}
      onAddProduct={onAddProduct}
      onRemoveProduct={onRemoveProduct}
      formatCurrency={formatCurrency}
      theme={theme}
      t={t}
      depositBlocked={depositBlocked}
    />
  );
}

function SizedProductCard({
  product,
  productName,
  price,
  variantLabel,
  getAddedQty,
  getAvailCount,
  getSelectedQty,
  onAddProduct,
  onRemoveProduct,
  formatCurrency,
  theme,
  t,
  depositBlocked
}) {
  const [showSizes, setShowSizes] = useState(false);
  const totalAdded = (product.sizes || []).reduce(
    (sum, s) => sum + getAddedQty(product.id, getSizeValue(s)), 0
  );

  return (
    <div className="bg-gray-50 rounded-xl overflow-hidden">
      <button
        onClick={() => setShowSizes(!showSizes)}
        className="w-full p-3 flex items-center gap-3 text-left hover:bg-gray-100 transition-colors"
      >
        {product.picture_url && (
          <img
            src={product.picture_url}
            alt={productName}
            className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 truncate text-sm">{productName}</p>
          <div className="flex items-center gap-2">
            {price != null && (
              <span className="text-xs text-gray-500">
                {formatCurrency(price)}/{t('manage.each', 'ea')}
              </span>
            )}
            <span className="text-xs text-gray-600">{variantLabel}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {totalAdded > 0 && (
            <span
              className="text-xs font-semibold px-2 py-0.5 rounded-full text-white"
              style={{ backgroundColor: theme.colors.primary.base }}
            >
              {totalAdded}
            </span>
          )}
          <SafeIcon
            icon={FiChevronDown}
            className={`w-4 h-4 text-gray-600 transition-transform ${showSizes ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {showSizes && (
        <div className="border-t border-gray-200 px-3 pb-3 pt-2 space-y-2">
          {product.sizes.map((sizeOption) => {
            const sv = getSizeValue(sizeOption);
            const avail = getAvailCount(product.id, sv);
            const qty = getAddedQty(product.id, sv);
            const selected = getSelectedQty(product.id, sv);
            const canAdd = avail > 0 && !depositBlocked;

            return (
              <div
                key={sv}
                className="flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-white transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{sv}</span>
                    {typeof sizeOption === 'object' && sizeOption?.description && (
                      <span className="text-xs text-gray-600">{sizeOption.description}</span>
                    )}
                  </div>
                  <AvailabilityLabel avail={avail} selected={selected} t={t} />
                </div>
                <div className="flex items-center gap-2">
                  {qty > 0 && (
                    <button
                      onClick={() => onRemoveProduct(product.id, sv)}
                      className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
                    >
                      <SafeIcon icon={FiMinus} className="w-3.5 h-3.5 text-gray-600" />
                    </button>
                  )}
                  <span className="w-6 text-center font-semibold text-sm text-gray-900">{qty}</span>
                  <button
                    onClick={() => onAddProduct(product, sv)}
                    disabled={!canAdd}
                    className="w-7 h-7 rounded-full flex items-center justify-center transition-colors text-white disabled:opacity-30"
                    style={{ backgroundColor: theme.colors.primary.base }}
                  >
                    <SafeIcon icon={FiPlus} className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

async function fetchAvailability(locationId, startDate, endDate, excludeBookingId, excludedInventoryItemIds) {
  const map = {};
  let entries = [];
  try {
    const params = new URLSearchParams({ locationId, startDate, endDate });
    if (excludeBookingId) params.set('excludeBookingId', excludeBookingId);
    if (excludedInventoryItemIds && excludedInventoryItemIds.length > 0) {
      params.set('excludedInventoryItemIds', excludedInventoryItemIds.join(','));
    }
    const res = await fetch(`${API_BASE}/availability?${params}`);
    if (!res.ok) return { map, entries };
    const data = await res.json();
    entries = data.availability || [];
    entries.forEach(entry => {
      const key = `${entry.product_type_id}_${entry.size || 'null'}`;
      map[key] = entry.available_count || 0;
    });
  } catch {
    // return empty result on error
  }
  return { map, entries };
}

// Delegates to the shared matcher so this screen can't drift from the edit
// screen's pricing. It previously carried its own copy, which coerced a NULL
// calculated_hours into the raw duration_value and so read a "9 Days" rule as
// 9 hours — see the ruleHours() note in editFlowUtils.js.
//
// The last-resort fallback below is preserved deliberately: unlike EditFlow,
// this screen has no "duration exceeds every rule" guard, so returning null here
// would leave an added product with no price at all. Falling back to the longest
// rule is still wrong (it should block, as EditFlow does) — tracked as a
// follow-up rather than changed here, since it is a separate behaviour decision.
function findMatchingPricingRule(pricingOptions, startDate, endDate) {
  const match = findBestPricingMatch(pricingOptions, startDate, endDate);
  if (match) return match;

  if (!pricingOptions || pricingOptions.length === 0) return null;
  const longest = pricingOptions
    .filter(o => Number.isFinite(parseFloat(o?.calculated_hours)) && parseFloat(o.calculated_hours) > 0)
    .sort((a, b) => parseFloat(a.calculated_hours) - parseFloat(b.calculated_hours))
    .pop();
  return longest || null;
}
